// src/services/auditReportService.js
import { supabase, supabaseAdmin } from '../lib/supabase';
import toast from 'react-hot-toast';

// ─── NOTE: deckSubmissionService imported inline inside publishAuditReport
// to avoid circular dependency issues
import { deckSubmissionService } from './deckSubmissionService';

// ─── UUID validator ───────────────────────────────────────────────────────────
const isValidUUID = (str) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str);

// ─── Schema error detector ────────────────────────────────────────────────────
function isSchemaError(error) {
  if (!error) return false;
  if (error?.code && typeof error?.code === 'string') {
    const errorClass = error?.code?.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') return true;
    if (errorClass === '23') return false;
  }
  if (error?.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];
    return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
  }
  return false;
}

// ─── JSON helpers ─────────────────────────────────────────────────────────────
function extractScoreFromJSON(jsonData) {
  try {
    if (jsonData?.cover?.SCORE) return parseInt(jsonData.cover.SCORE, 10);
    if (jsonData?.score_overview?.MAIN_SCORE) return parseInt(jsonData.score_overview.MAIN_SCORE, 10);
    return null;
  } catch { return null; }
}

function extractCompanyNameFromJSON(jsonData) {
  try { return jsonData?.cover?.COMPANY_NAME || null; } catch { return null; }
}

function extractDeckNameFromJSON(jsonData) {
  try { return jsonData?.cover?.DECK_NAME || null; } catch { return null; }
}

function extractSlideCountFromJSON(jsonData) {
  try {
    return jsonData?.cover?.TOTAL_SLIDES || jsonData?.slide_analysis?.slides?.length || null;
  } catch { return null; }
}

function extractReportDateFromJSON(jsonData) {
  try { return jsonData?.meta?.REPORT_DATE || null; } catch { return null; }
}

function extractReportTypeFromJSON(jsonData) {
  try { return jsonData?.report_type || 'standard'; } catch { return 'standard'; }
}

function sanitizeFilename(name) {
  return name.replace(/[^a-zA-Z0-9]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
}

function getYearMonthPath() {
  const now = new Date();
  return `${now.getFullYear()}/${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function generateReportId() {
  const timestamp = Date.now();
  const randomStr = Math.random().toString(36).substr(2, 9).toUpperCase();
  return `RPT-${timestamp}-${randomStr}`;
}

function enrichJsonWithReportId(jsonData, reportId) {
  if (!jsonData) return jsonData;
  return {
    ...jsonData,
    report_id: reportId,
    meta: {
      ...jsonData.meta,
      REPORT_ID: reportId,
      REPORT_ID_GENERATED: new Date().toISOString(),
    },
  };
}

// ─── Service ──────────────────────────────────────────────────────────────────
export const auditReportService = {

  // Upload audit HTML and map to user
  async uploadAuditReport(reportData) {
    try {
      const { data: { user } } = await supabaseAdmin?.auth?.getUser();
      if (!user) throw new Error('User not authenticated');

      const reportId = reportData?.reportId || generateReportId();
      const enrichedJsonData = reportData?.jsonData
        ? enrichJsonWithReportId(reportData.jsonData, reportId)
        : null;

      const report = {
        submission_id: reportData?.submissionId,
        user_id: reportData?.userId,
        html_content: reportData?.htmlContent,
        file_name: reportData?.fileName,
        file_size: reportData?.fileSize,
        uploaded_by: user?.id,
        json_data: enrichedJsonData,
        company_name: reportData?.companyName || null,
        deck_name: reportData?.deckName || null,
        total_score: reportData?.totalScore || null,
        total_slides: reportData?.totalSlides || null,
        report_type: reportData?.reportType || 'standard',
        report_date: reportData?.reportDate || null,
        report_id: reportId,
      };

      const { data, error } = await supabaseAdmin
        ?.from('audit_reports')
        ?.insert([report])
        ?.select()
        ?.single();

      if (error) throw error;

      console.log('✅ Audit report uploaded with ID:', reportId);
      return { ...data, report_id: reportId };
    } catch (error) {
      console.error('Error uploading audit report:', error);
      throw error;
    }
  },

  // Upload JSON audit report with year/month structure
  async uploadJSONAuditReport(jsonData, file = null, submissionId = null, userId = null, reportId = null) {
    try {
      const { data: { user } } = await supabaseAdmin?.auth?.getUser();
      if (!user) throw new Error('User not authenticated');

      const uniqueReportId = reportId || generateReportId();
      const enrichedJsonData = enrichJsonWithReportId(jsonData, uniqueReportId);

      const companyName = extractCompanyNameFromJSON(enrichedJsonData) || 'unknown';
      const deckName = extractDeckNameFromJSON(enrichedJsonData);
      const totalScore = extractScoreFromJSON(enrichedJsonData);
      const totalSlides = extractSlideCountFromJSON(enrichedJsonData);
      const reportDate = extractReportDateFromJSON(enrichedJsonData);
      const reportType = extractReportTypeFromJSON(enrichedJsonData);

      let filePath = null;
      let fileUrl = null;

      if (file) {
        const timestamp = Date.now();
        const yearMonth = getYearMonthPath();
        const safeCompany = sanitizeFilename(companyName);
        const safeFile = sanitizeFilename(file.name.replace('.json', ''));
        const fileName = `${safeCompany}_${timestamp}_${safeFile}_${uniqueReportId}.json`;
        const fullPath = `${yearMonth}/${fileName}`;

        const { data: storageData, error: storageError } = await supabaseAdmin
          .storage.from('audit-files')
          .upload(fullPath, file, { cacheControl: '3600', contentType: 'application/json' });

        if (storageError) throw storageError;
        filePath = storageData.path;

        const { data: { publicUrl } } = supabaseAdmin
          .storage.from('audit-files').getPublicUrl(filePath);
        fileUrl = publicUrl;
      }

      const report = {
        submission_id: submissionId,
        user_id: userId || user?.id,
        html_content: null,
        json_data: enrichedJsonData,
        file_name: file?.name || null,
        file_path: filePath,
        file_url: fileUrl,
        file_size: file?.size || null,
        uploaded_by: user?.id,
        company_name: companyName,
        deck_name: deckName,
        total_score: totalScore,
        total_slides: totalSlides,
        report_type: reportType,
        report_date: reportDate,
        status: 'pending',
        published_at: null,
        report_id: uniqueReportId,
      };

      const { data, error } = await supabaseAdmin
        ?.from('audit_reports')
        ?.insert([report])
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        ...data,
        report_id: uniqueReportId,
        storageInfo: filePath ? { path: filePath, url: fileUrl } : null,
      };
    } catch (error) {
      console.error('Error uploading JSON audit report:', error);
      throw error;
    }
  },

  // Get audit report for current user
  async getUserAuditReport() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        ?.from('audit_reports')
        ?.select(`*, deck_submissions!audit_reports_submission_id_fkey (title, file_name)`)
        ?.eq('user_id', user?.id)
        ?.not('published_at', 'is', null)
        ?.order('published_at', { ascending: false })
        ?.limit(1)
        ?.maybeSingle();

      if (error && error?.code !== 'PGRST116') {
        if (isSchemaError(error)) throw error;
        return null;
      }
      return data;
    } catch (error) {
      console.error('Error fetching user audit report:', error);
      throw error;
    }
  },

  // Get all audit reports for current user
  async getUserAuditReports(userId) {
    try {
      if (!userId) throw new Error('User ID is required');

      const { data, error } = await supabase
        ?.from('audit_reports')
        ?.select(`
          *,
          deck_submissions!audit_reports_submission_id_fkey (
            id, title, file_name, request_id, audit_type
          )
        `)
        ?.eq('user_id', userId)
        ?.not('published_at', 'is', null)
        ?.order('published_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return data || [];
    } catch (error) {
      console.error('Error fetching user audit reports:', error);
      throw error;
    }
  },

  // Get audit report by ID
  async getAuditReportById(id) {
    try {
      const { data, error } = await supabaseAdmin
        ?.from('audit_reports')
        ?.select(`*, deck_submissions!audit_reports_submission_id_fkey (*)`)
        ?.eq('id', id)
        ?.single();

      if (error) {
        if (isSchemaError(error)) throw error;
        return null;
      }
      return data;
    } catch (error) {
      console.error('Error fetching audit report by ID:', error);
      throw error;
    }
  },

  // Get audit report by report ID
  async getAuditReportByReportId(reportId) {
    try {
      const { data, error } = await supabaseAdmin
        ?.from('audit_reports')
        ?.select(`*, deck_submissions!audit_reports_submission_id_fkey (*)`)
        ?.eq('report_id', reportId)
        ?.single();

      if (error) {
        if (error?.code === 'PGRST116') return null;
        if (isSchemaError(error)) throw error;
        return null;
      }
      return data;
    } catch (error) {
      console.error('Error fetching audit report by report ID:', error);
      throw error;
    }
  },

  async publishAuditReport(auditId) {
    try {
      if (!auditId || !isValidUUID(auditId)) {
        const msg = `Cannot publish: invalid audit ID "${auditId}"`;
        console.error('❌', msg);
        toast.error(msg);
        throw new Error(msg);
      }

      console.log('📤 Publishing audit report:', auditId);

      const { data: reportData, error: fetchError } = await supabaseAdmin
        .from('audit_reports')
        .select('id, submission_id, html_content, json_data, html_url, report_id')
        .eq('id', auditId)
        .single();

      if (fetchError) {
        console.error('❌ Failed to fetch audit report:', fetchError);
        throw fetchError;
      }

      let decxScore = null;
      if (reportData?.json_data) {
        decxScore = extractScoreFromJSON(reportData.json_data);
      } else if (reportData?.html_content) {
        const scorePatterns = [
          /<div[^>]*class=["']score["'][^>]*>(\d+)(?:\/100)?<\/div>/i,
          /DecX Score[:\s]+(\d+)(?:\/100)?/i,
          /(\d+)\/100/,
          /score["']?[:\s]+(\d+)/i,
        ];
        for (const pattern of scorePatterns) {
          const match = reportData.html_content.match(pattern);
          if (match?.[1]) {
            const score = parseInt(match[1], 10);
            if (score >= 0 && score <= 100) { decxScore = score; break; }
          }
        }
      }

      const { data, error: updateError } = await supabaseAdmin
        .from('audit_reports')
        .update({
          published_at: new Date().toISOString(),
          status: 'published',
        })
        .eq('id', auditId)
        .select()
        .single();

      if (updateError) {
        console.error('❌ Failed to update audit_reports:', updateError);
        throw updateError;
      }

      console.log('✅ audit_reports updated to published');

      const submissionId = reportData?.submission_id;

      if (submissionId && isValidUUID(submissionId)) {
        const submissionUpdates = {
          status: 'completed',
          completed_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };

        if (decxScore !== null) submissionUpdates.decx_score = decxScore;
        if (reportData?.report_id) submissionUpdates.report_id = reportData.report_id;

        const { error: subError } = await supabaseAdmin
          .from('deck_submissions')
          .update(submissionUpdates)
          .eq('id', submissionId);

        if (subError) {
          console.error('⚠️ deck_submission update failed (non-fatal):', subError);
        } else {
          console.log('✅ deck_submission marked as completed');
        }
      } else {
        console.warn('⚠️ No valid submissionId — skipping deck_submission update');
      }

      toast.success(
        `Audit published successfully${reportData?.report_id ? ` (${reportData.report_id})` : ''}`,
        { icon: '✅' }
      );

      return data;
    } catch (error) {
      console.error('Error publishing audit report:', error);
      toast.error('Failed to publish audit report');
      throw error;
    }
  },

  // ─── UPDATED: getAllAuditReports ───────────────────────────────────────────
  // Now fetches paid_audit_orders and maps paymentStatus + razorpayPaymentId
  // onto each audit row via deck_submissions.payment_order_id
  async getAllAuditReports() {
    try {
      const { data: usersData, error: usersError } = await supabaseAdmin
        ?.from('user_profiles')
        ?.select('id, email, full_name, role, subscription_status, created_at, updated_at')
        ?.order('created_at', { ascending: false });

      if (usersError) {
        if (isSchemaError(usersError)) throw usersError;
        return [];
      }
      if (!usersData || usersData?.length === 0) return [];

      const userIds = usersData.map(u => u.id);

      // ── Fetch all 3 tables in parallel ──────────────────────────────────
      const [submissionsRes, reportsRes, ordersRes] = await Promise.all([
        supabaseAdmin.from('deck_submissions').select('*').in('user_id', userIds),
        supabaseAdmin.from('audit_reports').select('*').in('user_id', userIds),
        supabaseAdmin
          .from('paid_audit_orders')
          .select('id, user_id, submission_id, payment_status, razorpay_payment_id, razorpay_order_id, amount, paid_at'),
        // ✅ Removed .in('user_id', userIds) — fetch ALL orders, not filtered by admin userIds
        // This fixes the case where report.user_id = admin but order.user_id = Dipak
      ]);

      // ── Build lookup maps ────────────────────────────────────────────────
      const submissionsMap = {};
      const reportsMap = {};
      const orderByIdMap = {}; // paid_audit_orders.id → order
      const orderBySubMap = {}; // paid_audit_orders.submission_id → order

      submissionsRes.data?.forEach(sub => {
        if (!submissionsMap[sub.user_id]) submissionsMap[sub.user_id] = [];
        submissionsMap[sub.user_id].push(sub);
      });

      reportsRes.data?.forEach(report => {
        if (!reportsMap[report.user_id]) reportsMap[report.user_id] = [];
        reportsMap[report.user_id].push(report);
      });

      ordersRes.data?.forEach(order => {
        if (order.id) orderByIdMap[order.id] = order;
        if (order.submission_id) orderBySubMap[order.submission_id] = order;
      });

      // ── Helper: get payment info for a submission ────────────────────────
      const getPaymentInfo = (submission) => {
        if (!submission) return { paymentStatus: null, razorpayPaymentId: null };

        // Primary: match via deck_submissions.payment_order_id → paid_audit_orders.id
        let order = submission.payment_order_id
          ? orderByIdMap[submission.payment_order_id]
          : null;

        // Fallback: match via paid_audit_orders.submission_id
        if (!order) order = orderBySubMap[submission.id] || null;

        if (!order) return { paymentStatus: null, razorpayPaymentId: null };

        return {
          paymentStatus: order.payment_status || null,
          razorpayPaymentId: order.razorpay_payment_id || null,
          razorpayOrderId: order.razorpay_order_id || null,
          paidAt: order.paid_at || null,
          paymentAmount: order.amount || null,
        };
      };

      // ── Helper: get best payment info for a user ─────────────────────────
      // Used when audit report's submission_id doesn't match — scans all user
      // submissions to find any captured payment (handles post-payment upload)
      const getBestPaymentInfoForUser = (userSubmissions, preferredSub) => {
        // First try the preferred/matched submission
        const preferred = getPaymentInfo(preferredSub);
        if (preferred.paymentStatus) return preferred;

        // Fallback: scan all submissions for this user to find a captured payment
        for (const sub of userSubmissions) {
          const pi = getPaymentInfo(sub);
          if (pi.paymentStatus === 'captured' || pi.paymentStatus === 'received') return pi;
        }

        // Last resort: return any payment status found (even pending/failed)
        for (const sub of userSubmissions) {
          const pi = getPaymentInfo(sub);
          if (pi.paymentStatus) return pi;
        }

        return { paymentStatus: null, razorpayPaymentId: null };
      };

      // ── Build result ─────────────────────────────────────────────────────
      const result = [];

      usersData.forEach(user => {
        const userSubmissions = submissionsMap[user.id] || [];
        const userReports = reportsMap[user.id] || [];

        if (userReports.length > 0) {
          userReports.forEach(report => {
            const matchingSub = userSubmissions.find(s => s.id === report.submission_id);

            console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
            console.log('💳 report.id:', report.id);
            console.log('💳 report.submission_id:', report.submission_id);
            console.log('💳 matchingSub id:', matchingSub?.id ?? 'NOT FOUND');
            console.log('💳 matchingSub.payment_order_id:', matchingSub?.payment_order_id ?? 'NULL');
            console.log('💳 orderByIdMap keys:', Object.keys(orderByIdMap));
            console.log('💳 orderBySubMap keys:', Object.keys(orderBySubMap));
            console.log('💳 userSubmissions count:', userSubmissions.length);
            console.log('💳 userSubmissions ids:', userSubmissions.map(s => s.id));
            console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

            const paymentInfo = getBestPaymentInfoForUser(userSubmissions, matchingSub);
            console.log('💳 resolved paymentInfo:', paymentInfo);

            result.push({
              ...report,
              ...paymentInfo,
              user_profiles: user,
              deck_submissions: matchingSub || null,
            });
          });
        } else if (userSubmissions.length > 0) {
          const latest = [...userSubmissions].sort(
            (a, b) => new Date(b.submitted_at) - new Date(a.submitted_at)
          )[0];
          const paymentInfo = getPaymentInfo(latest);
          result.push({
            id: `pending-${latest.id}`,
            submission_id: latest.id,
            user_id: user.id,
            html_content: null,
            json_data: null,
            file_name: latest.file_name,
            file_size: latest.file_size,
            uploaded_by: null,
            uploaded_at: latest.submitted_at,
            created_at: latest.created_at,
            updated_at: latest.updated_at,
            published_at: null,
            status: 'pending',
            report_id: null,
            ...paymentInfo,
            user_profiles: user,
            deck_submissions: latest,
          });
        } else {
          result.push({
            id: `user-${user.id}`,
            submission_id: null,
            user_id: user.id,
            html_content: null,
            json_data: null,
            file_name: null,
            file_size: null,
            uploaded_by: null,
            uploaded_at: null,
            created_at: user.created_at,
            updated_at: user.updated_at,
            published_at: null,
            status: 'no_submission',
            report_id: null,
            paymentStatus: null,
            razorpayPaymentId: null,
            user_profiles: user,
            deck_submissions: null,
          });
        }
      });

      return result;
    } catch (error) {
      console.error('Error fetching all audit reports:', error);
      throw error;
    }
  },

  // Get all users for assignment dropdown
  async getAllUsers() {
    try {
      const { data, error } = await supabaseAdmin
        ?.from('user_profiles')
        ?.select('id, email, full_name, role, subscription_status, created_at, updated_at')
        ?.eq('role', 'user')
        ?.order('full_name', { ascending: true });

      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return data || [];
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  },

  // Get user details with submission history
  async getUserDetailsWithHistory(userId) {
    try {
      const [userProfile, submissions] = await Promise.all([
        supabaseAdmin?.from('user_profiles')?.select('*')?.eq('id', userId)?.single(),
        supabaseAdmin?.from('deck_submissions')?.select('*')?.eq('user_id', userId)?.order('submitted_at', { ascending: false }),
      ]);

      if (userProfile?.error) {
        if (isSchemaError(userProfile.error)) throw userProfile.error;
        return null;
      }

      return {
        ...userProfile.data,
        submissions: submissions?.data || [],
        submissionCount: submissions?.data?.length || 0,
      };
    } catch (error) {
      console.error('Error fetching user details:', error);
      throw error;
    }
  },

  // Delete audit report
  async deleteAuditReport(reportId) {
    try {
      if (reportId?.startsWith('pending-')) {
        const submissionId = reportId.replace('pending-', '');
        const { error } = await supabaseAdmin?.from('deck_submissions')?.delete()?.eq('id', submissionId);
        if (error) throw error;
        return;
      }
      if (reportId?.startsWith('user-')) return;

      const { data: auditReport, error: fetchError } = await supabaseAdmin
        ?.from('audit_reports')
        ?.select('submission_id, file_path, html_storage_path, report_id')
        ?.eq('id', reportId)
        ?.single();

      if (fetchError && fetchError?.code !== 'PGRST116') throw fetchError;

      if (auditReport?.file_path) {
        try { await supabaseAdmin.storage.from('audit-files').remove([auditReport.file_path]); } catch { }
      }
      if (auditReport?.html_storage_path) {
        try { await supabaseAdmin.storage.from('html-reports').remove([auditReport.html_storage_path]); } catch { }
      }

      const { error: deleteError } = await supabaseAdmin?.from('audit_reports')?.delete()?.eq('id', reportId);
      if (deleteError) throw deleteError;

      if (auditReport?.submission_id) {
        await supabaseAdmin?.from('deck_submissions')?.delete()?.eq('id', auditReport.submission_id);
      }

      console.log(`✅ Audit report ${reportId} deleted`);
    } catch (error) {
      console.error('Error deleting audit report:', error);
      throw error;
    }
  },

  // Delete user
  async deleteUser(userId) {
    try {
      const { data: auditReports } = await supabaseAdmin
        ?.from('audit_reports')
        ?.select('file_path, html_storage_path')
        ?.eq('user_id', userId);

      if (auditReports?.length > 0) {
        const jsonPaths = auditReports.filter(r => r.file_path).map(r => r.file_path);
        const htmlPaths = auditReports.filter(r => r.html_storage_path).map(r => r.html_storage_path);

        if (jsonPaths.length > 0) {
          await supabaseAdmin.storage.from('audit-files').remove(jsonPaths);
        }

        if (htmlPaths.length > 0) {
          await supabaseAdmin.storage.from('html-reports').remove(htmlPaths);
        }
      }

      await Promise.all([
        supabaseAdmin.from('deck_submissions').delete().eq('user_id', userId),
        supabaseAdmin.from('audit_reports').delete().eq('user_id', userId),
      ]);

      const { error: profileError } = await supabaseAdmin
        .from('user_profiles')
        .delete()
        .eq('id', userId);

      if (profileError) throw profileError;

      // 🔥 DELETE FROM AUTH
      const { error: authError } =
        await supabaseAdmin.auth.admin.deleteUser(userId);

      if (authError) {
        console.error("Auth delete error:", authError.message);
        // optional: don't throw to avoid partial failure
      }

    } catch (error) {
      console.error('Error deleting user:', error);
      throw error;
    }
  },

  // Update audit report status
  async updateAuditStatus(reportId, status, additionalData = {}) {
    try {
      const updates = { status, updated_at: new Date().toISOString(), ...additionalData };
      if (status === 'in_review') updates.review_started_at = new Date().toISOString();
      if (status === 'completed') updates.processed_at = new Date().toISOString();
      if (status === 'published') updates.published_at = new Date().toISOString();

      const { data, error } = await supabaseAdmin
        ?.from('audit_reports')?.update(updates)?.eq('id', reportId)?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating audit status:', error);
      throw error;
    }
  },

  // Get audit report with HTML URL
  async getAuditReportWithHtml(reportId) {
    try {
      const { data, error } = await supabaseAdmin
        ?.from('audit_reports')
        ?.select(`*, html_content!audit_reports_id_fkey (id, public_url, storage_path, bucket_name, created_at)`)
        ?.eq('id', reportId)
        ?.single();

      if (error) {
        if (isSchemaError(error)) throw error;
        return null;
      }
      return data;
    } catch (error) {
      console.error('Error fetching audit report with HTML:', error);
      throw error;
    }
  },

  // Get all pending audits
  async getPendingAudits() {
    try {
      const { data, error } = await supabaseAdmin
        ?.from('audit_reports')
        ?.select('*')
        ?.eq('status', 'pending')
        ?.is('html_generated', null)
        ?.order('created_at', { ascending: true });

      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return data || [];
    } catch (error) {
      console.error('Error fetching pending audits:', error);
      throw error;
    }
  },

  // Mark HTML as generated
  async markHtmlGenerated(reportId, htmlUrl, htmlStoragePath) {
    try {
      const { data, error } = await supabaseAdmin
        ?.from('audit_reports')
        ?.update({
          html_url: htmlUrl,
          html_storage_path: htmlStoragePath,
          html_generated: true,
          html_generated_at: new Date().toISOString(),
          status: 'completed',
        })
        ?.eq('id', reportId)
        ?.select()
        ?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error marking HTML as generated:', error);
      throw error;
    }
  },

  // Search audit reports
  async searchAuditReports(searchTerm) {
    try {
      const { data, error } = await supabaseAdmin
        ?.from('audit_reports')
        ?.select(`*, deck_submissions!audit_reports_submission_id_fkey (*)`)
        ?.or(`company_name.ilike.%${searchTerm}%,deck_name.ilike.%${searchTerm}%,report_id.ilike.%${searchTerm}%`)
        ?.order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return data || [];
    } catch (error) {
      console.error('Error searching audit reports:', error);
      throw error;
    }
  },

  async assignAuditToUser({ auditId, userId, submissionId, assignedAt, auditType }) {
    try {
      console.log('📤 assignAuditToUser:', { auditId, userId, submissionId });

      if (!auditId || !isValidUUID(auditId)) {
        throw new Error(`Invalid auditId: "${auditId}"`);
      }

      const { data: existing, error: fetchError } = await supabaseAdmin
        .from('audit_reports')
        .select('html_storage_path, html_url, report_type, json_data, report_id')
        .eq('id', auditId)
        .single();

      if (fetchError) throw fetchError;

      let htmlUrl = existing?.html_url;
      if (!htmlUrl && existing?.html_storage_path) {
        const { data: { publicUrl } } = supabaseAdmin
          .storage.from('html-reports').getPublicUrl(existing.html_storage_path);
        htmlUrl = publicUrl;
      }

      const finalAuditType =
        auditType || existing?.report_type || existing?.json_data?.report_type || 'free';

      const { data, error } = await supabaseAdmin
        .from('audit_reports')
        .update({
          user_id: userId,
          submission_id: submissionId || null,
          status: 'pending',
          published_at: null,
          updated_at: new Date().toISOString(),
          html_url: htmlUrl || null,
          report_type: finalAuditType,
        })
        .eq('id', auditId)
        .select()
        .single();

      if (error) throw error;

      console.log('✅ Audit assigned, status = pending');

      if (submissionId && isValidUUID(submissionId)) {
        const { data: subExists } = await supabaseAdmin
          .from('deck_submissions')
          .select('id, status')
          .eq('id', submissionId)
          .maybeSingle();

        if (subExists) {
          const { error: subError } = await supabaseAdmin
            .from('deck_submissions')
            .update({
              status: 'pending',
              updated_at: new Date().toISOString(),
              audit_type: finalAuditType,
            })
            .eq('id', submissionId);

          if (subError) {
            console.error('⚠️ deck_submission update failed (non-fatal):', subError);
          } else {
            console.log('✅ deck_submission stays pending');
          }
        }
      }

      return data;
    } catch (error) {
      console.error('❌ Error assigning audit to user:', error);
      throw error;
    }
  },

  // Get audit statistics
  async getAuditStats() {
    try {
      const { data, error } = await supabaseAdmin
        ?.from('audit_reports')
        ?.select('status, report_type, created_at');

      if (error) throw error;

      return {
        total: data?.length || 0,
        pending: data?.filter(r => r.status === 'pending').length || 0,
        processing: data?.filter(r => r.status === 'processing').length || 0,
        completed: data?.filter(r => r.status === 'completed').length || 0,
        published: data?.filter(r => r.status === 'published').length || 0,
        byType: {
          free: data?.filter(r => r.report_type === 'free').length || 0,
          standard: data?.filter(r => r.report_type === 'standard').length || 0,
          premium: data?.filter(r => r.report_type === 'premium').length || 0,
        },
      };
    } catch (error) {
      console.error('Error fetching audit stats:', error);
      throw error;
    }
  },
};